import type { BuildCookieOptions, UtmAttribution, UtmCookiePayload } from './types';
import {
  COOKIE_DEFAULT_DOMAIN,
  COOKIE_MAX_AGE,
  COOKIE_NAME,
  isValidUtm,
} from './types';

/* -------------------------------------------------------------------------- */
/*  Encode / decode (cookie value <-> payload)                                */
/* -------------------------------------------------------------------------- */

/**
 * Serialize a payload to the URI-encoded JSON string we store in the cookie.
 * `null` / `undefined` fields are omitted; we never persist falsy noise.
 */
function encodeCookieValue(utm: UtmAttribution): string {
  const trimmed: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(utm)) {
    if (value === null || value === undefined) continue;
    trimmed[key] = value;
  }
  return encodeURIComponent(JSON.stringify(trimmed));
}

/**
 * Parse a raw cookie value back into a `UtmCookiePayload`.
 * Returns `null` for malformed JSON or payloads without `utm_source`.
 */
function decodeCookieValue(raw: string): UtmCookiePayload | null {
  try {
    const decoded = decodeURIComponent(raw);
    const parsed = JSON.parse(decoded) as unknown;
    return isValidUtm(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * 从 Cookie 字符串读取第一个同名字段。
 * 保留原始编码值，具体 payload 的解码和校验由所属业务模块负责。
 */
export function readRawCookieValue(
  cookieHeader: string | null | undefined,
  cookieName: string,
): string | null {
  if (!cookieHeader) return null;

  const segments = cookieHeader.split(';');
  for (const segment of segments) {
    const trimmed = segment.trim();
    if (!trimmed.startsWith(`${cookieName}=`)) continue;
    const rawValue = trimmed.slice(cookieName.length + 1);
    return rawValue || null;
  }
  return null;
}

/* -------------------------------------------------------------------------- */
/*  Worker IO (Cloudflare / generic Request/Response)                         */
/* -------------------------------------------------------------------------- */

/**
 * Read `enter_utm` from a `Cookie` header (Worker / Edge runtime).
 *
 * If the same cookie name appears twice (e.g. legacy host-scoped + new
 * `.converge.ai`-scoped during migration), we take the first segment. The
 * legacy host-scoped cookie is expected to age out naturally.
 */
export function readFromCookieHeader(headers: Headers): UtmCookiePayload | null {
  const cookieHeader = headers.get('Cookie') ?? headers.get('cookie');
  const rawValue = readRawCookieValue(cookieHeader, COOKIE_NAME);
  return rawValue ? decodeCookieValue(rawValue) : null;
}

/**
 * Resolve `domain` honouring caller intent: explicit `undefined` means
 * "host-scoped cookie, no Domain attribute". A missing `domain` key falls back
 * to `COOKIE_DEFAULT_DOMAIN`. We can't use destructuring defaults here because
 * those would also rewrite an explicit `undefined` back to the default.
 */
function resolveDomain(options: BuildCookieOptions): string | undefined {
  return 'domain' in options ? options.domain : COOKIE_DEFAULT_DOMAIN;
}

/**
 * 构造单个 Set-Cookie 值，供 UTM JSON 和独立的点击 ID cookie 复用同一套作用域策略。
 */
export function buildRawSetCookieHeader(
  cookieName: string,
  encodedValue: string,
  options: BuildCookieOptions = {},
): string {
  const domain = resolveDomain(options);
  const { maxAge = COOKIE_MAX_AGE, sameSite = 'Lax', secure = true, path = '/' } = options;
  const parts = [`${cookieName}=${encodedValue}`, `Max-Age=${maxAge}`, `Path=${path}`];
  if (domain) parts.push(`Domain=${domain}`);
  parts.push(`SameSite=${sameSite}`);
  if (secure) parts.push('Secure');
  return parts.join('; ');
}

/**
 * Build a single `Set-Cookie` header value for `enter_utm`.
 *
 * Defaults match the production attribution policy:
 *   - Domain  = `.converge.ai`  (cross-subdomain)
 *   - Max-Age = 30 days
 *   - Path    = `/`
 *   - SameSite=Lax, Secure
 *
 * For local development pass `{ domain: undefined }` so the browser stores a
 * host-scoped cookie that the local origin can actually read.
 */
export function buildSetCookieHeader(
  utm: UtmAttribution,
  options: BuildCookieOptions = {},
): string {
  const value = encodeCookieValue(utm);
  return buildRawSetCookieHeader(COOKIE_NAME, value, options);
}

/* -------------------------------------------------------------------------- */
/*  Browser IO (document.cookie)                                              */
/* -------------------------------------------------------------------------- */

function isBrowser(): boolean {
  return typeof document !== 'undefined';
}

/**
 * 从 document.cookie 读取原始 cookie 值。浏览器禁用 cookie 或访问抛错时按缺失处理。
 */
export function readRawCookieFromDocument(cookieName: string): string | null {
  if (!isBrowser()) return null;

  try {
    return readRawCookieValue(document.cookie, cookieName);
  } catch {
    return null;
  }
}

/**
 * Read `enter_utm` from `document.cookie`. Returns `null` outside a browser
 * environment (e.g. SSR, Node).
 */
export function readFromDocument(): UtmCookiePayload | null {
  const rawValue = readRawCookieFromDocument(COOKIE_NAME);
  return rawValue ? decodeCookieValue(rawValue) : null;
}

/**
 * Write (or clear) `enter_utm` via `document.cookie`. No-op outside a browser.
 *
 * Pass `null` to clear the cookie (Max-Age=0).
 */
export function writeToDocument(
  utm: UtmAttribution | null,
  options: BuildCookieOptions = {},
): void {
  if (!isBrowser()) return;

  if (utm === null) {
    const domain = resolveDomain(options);
    const { sameSite = 'Lax', secure = true, path = '/' } = options;
    const parts = [`${COOKIE_NAME}=`, `Max-Age=0`, `Path=${path}`];
    if (domain) parts.push(`Domain=${domain}`);
    parts.push(`SameSite=${sameSite}`);
    if (secure) parts.push('Secure');
    document.cookie = parts.join('; ');
    return;
  }

  document.cookie = buildSetCookieHeader(utm, options);
}

/* -------------------------------------------------------------------------- */
/*  Host-aware option resolvers                                               */
/* -------------------------------------------------------------------------- */

/**
 * Returns true when the hostname can host a `Domain=.converge.ai` cookie
 * (i.e. it equals or is a subdomain of `converge.ai`). Browsers reject
 * `Domain` attributes that don't match the document origin, so callers must
 * fall back to host-scoped cookies on any other host (localhost, *.workers.dev
 * preview, the legacy enter.pro domain during migration, etc.).
 */
function isConvergeHost(hostname: string): boolean {
  return hostname === 'converge.ai' || hostname.endsWith('.converge.ai');
}

/**
 * Compute `BuildCookieOptions` from the inbound Worker request.
 *
 *  - Production / staging on `*.converge.ai` -> `Domain=.converge.ai`, Secure.
 *  - Anything else (local wrangler dev, preview hostnames, legacy enter.pro)
 *    -> host-scoped cookie, Secure honoured by the request scheme.
 *
 * Caller can still override individual fields by spreading the result.
 */
export function resolveWorkerCookieOptions(request: Request): BuildCookieOptions {
  let hostname = '';
  let secure = true;
  try {
    const url = new URL(request.url);
    hostname = url.hostname;
    secure = url.protocol === 'https:';
  } catch {
    return { domain: undefined, secure: false };
  }
  return {
    domain: isConvergeHost(hostname) ? COOKIE_DEFAULT_DOMAIN : undefined,
    secure,
  };
}

/**
 * Browser-side counterpart of `resolveWorkerCookieOptions`. Reads
 * `window.location` so the same `enter_utm` cookie that the Worker writes
 * can be safely rewritten from the browser without scope mismatch.
 *
 * Returns an empty options object when `window` is not available so SSR
 * callers receive the package defaults.
 */
export function resolveBrowserCookieOptions(): BuildCookieOptions {
  if (typeof window === 'undefined' || !window.location) return {};
  const { hostname, protocol } = window.location;
  return {
    domain: isConvergeHost(hostname) ? COOKIE_DEFAULT_DOMAIN : undefined,
    secure: protocol === 'https:',
  };
}
